// src/features/map/layers/WeatherPanel.jsx
// Weather/AQI detail drawer opened from WeatherChip. Portals to document.body
// (mirrors AircraftPanel's chrome) and fetches its OWN point bundle with
// forecast=1 — the chip's cached payload is current-only, to keep the
// moveend/interval refetch cheap.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CloudSun, X, Wind, Droplets, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { layers as layersApi } from '@/lib/trackizy';
import { aqiSeverity, groupForecastByDay, iconEmoji } from './weatherFormat';

// `at` / `day` are UTC-normalized (server) / local-date-derived (weatherFormat.js)
// values — render them in the viewer's local time/date, matching AircraftPanel's
// fmtTime convention.
function fmtSlotTime(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fmtDayHeader(day) {
  const d = new Date(`${day}T12:00:00`);
  return Number.isNaN(d.getTime()) ? day : d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-xs py-1">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-700 dark:text-slate-200 font-medium"><bdi>{value ?? '—'}</bdi></span>
    </div>
  );
}

export default function WeatherPanel({ point, lang, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null); // { current, aqi, forecast }
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    if (!point?.lat || !point?.lon) return undefined;
    let stale = false;
    setData(null);
    layersApi.weatherPoint(point.lat, point.lon, { forecast: 1, lang })
      .then((res) => { if (!stale) setData(res); })
      .catch(() => { /* panel renders empty sections on failure */ });
    return () => { stale = true; };
  }, [point?.lat, point?.lon, lang]);

  const { current, aqi, forecast } = data || {};
  const severity = aqi ? aqiSeverity(aqi.index) : null;
  const days = groupForecastByDay(forecast || []);

  return createPortal(
    <div
      className="fixed top-14 bottom-4 end-3 w-[300px] z-40 rounded-2xl shadow-xl overflow-y-auto p-4"
      style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <CloudSun size={16} className="text-sky-500" />
        <div className="flex-1 min-w-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
          {t('weather.title', 'Weather')}
        </div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={14} /></button>
      </div>

      {current && (
        <div className="rounded-xl bg-slate-100/60 dark:bg-slate-800/60 p-3 mb-3">
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">
              <bdi>{Math.round(current.tempC)}°</bdi>
            </div>
            <span className="text-3xl">{iconEmoji(current.icon)}</span>
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 capitalize mt-0.5"><bdi>{current.desc}</bdi></div>
          <Row label={t('weather.feelsLike', 'Feels like')} value={current.feelsLikeC != null ? `${Math.round(current.feelsLikeC)}°` : null} />
          <Row
            label={t('weather.wind', 'Wind')}
            value={current.windMs != null ? `${current.windMs} m/s${current.windDeg != null ? `, ${current.windDeg}°` : ''}` : null}
          />
          <Row label={t('weather.humidity', 'Humidity')} value={current.humidity != null ? `${current.humidity}%` : null} />
          <Row label={t('weather.visibility', 'Visibility')} value={current.visibilityM != null ? `${(current.visibilityM / 1000).toFixed(1)} km` : null} />
        </div>
      )}

      {aqi && (
        <div className="mb-3">
          <div className="flex items-center gap-2 mb-1.5">
            <Droplets size={13} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{t('weather.airQuality', 'Air quality')}</span>
            <span
              className="ms-auto text-[10px] font-bold px-2 py-0.5 rounded-full text-white"
              style={{ background: severity.color }}
            >
              {t(severity.labelKey)}
            </span>
          </div>
          <Row label="PM2.5" value={aqi.pm25 != null ? `${aqi.pm25} µg/m³` : null} />
          <Row label="PM10" value={aqi.pm10 != null ? `${aqi.pm10} µg/m³` : null} />
          <Row label="O₃" value={aqi.o3 != null ? `${aqi.o3} µg/m³` : null} />
          <Row label="NO₂" value={aqi.no2 != null ? `${aqi.no2} µg/m³` : null} />
          {aqi.pm10 > 100 && (
            <div className="flex items-start gap-1.5 mt-2 p-2 rounded-lg bg-amber-100/70 dark:bg-amber-900/30 text-[11px] text-amber-700 dark:text-amber-300">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>{t('weather.dustWarning', 'High dust levels — reduced visibility likely')}</span>
            </div>
          )}
        </div>
      )}

      {days.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Wind size={13} className="text-slate-400" />
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{t('weather.forecast', 'Forecast')}</span>
          </div>
          {days.map((d) => (
            <div key={d.day} className="border-b border-slate-200/60 dark:border-slate-700/60 last:border-0">
              <button
                type="button"
                onClick={() => setExpandedDay((v) => (v === d.day ? null : d.day))}
                className="w-full flex items-center gap-2 py-1.5 text-xs text-slate-600 dark:text-slate-300"
              >
                {expandedDay === d.day ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className="flex-1 text-start"><bdi>{fmtDayHeader(d.day)}</bdi></span>
                <span>{iconEmoji(d.icon)}</span>
                <span className="font-medium">
                  <bdi>{d.minC != null ? Math.round(d.minC) : '—'}°–{d.maxC != null ? Math.round(d.maxC) : '—'}°</bdi>
                </span>
              </button>
              {expandedDay === d.day && (
                <div className="pb-2 ps-5 space-y-1">
                  {d.slots.map((s) => (
                    <div key={s.at} className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                      <span><bdi>{fmtSlotTime(s.at)}</bdi></span>
                      <span>{iconEmoji(s.icon)}</span>
                      <span><bdi>{s.tempC != null ? Math.round(s.tempC) : '—'}°</bdi></span>
                      <span>{s.pop > 0 ? <bdi>{Math.round(s.pop * 100)}%</bdi> : null}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
